import { ROUTE_BACK_OFFICE_LOGIN, ROUTE_LOGIN } from '../constants';
import * as apiConstants from './apiUtilsConstants';

import axios from 'axios';
import store from '../redux/store';
import { login } from '../redux/authSlice';
import { clientLogger } from '../microscope/LoggerBoundary';
import { captureErrorReplay } from '../lib/replay';

const RECOVERABLE_PLATFORM_AUTH_ERROR_CODES = new Set([
  'META_AUTH_EXPIRED',
  'META_IDENTITY_CONFIRMATION_REQUIRED',
  'IG_AUTH_EXPIRED',
]);

function shouldBypassLoginRedirect(error) {
  const errorCode = error?.response?.data?.errorCode;
  return RECOVERABLE_PLATFORM_AUTH_ERROR_CODES.has(errorCode);
}

export * from './contentApiUtils';
export * from './authApiUtils';
export * from './entityApiUtils';
export * from './storageApiUtils';
export * from './assetApiUtils';
export * from './linkApiUtils';

axios.interceptors.response.use(
  async (response) => {
    if (response.data?.user) {
      store.dispatch(login(response.data.user));
    }
    return response;
  },
  error => {
    if (error?.response?.status >= 500) {
      const replayId = captureErrorReplay();
      clientLogger.error(`API Error: ${error.response.status} ${error.response.config?.method?.toUpperCase()} ${error.response.config?.url}`, {
        replay_id: replayId,
        statusCode: error.response.status,
        method: error.response.config?.method,
        url: error.response.config?.url,
        stack: error.response.data?.stack || error.stack || null,
        responseData: error.response.data,
      });
    } else if (error?.response?.status >= 400) {
      clientLogger.warn(`API Error: ${error.response.status} ${error.response.config?.method?.toUpperCase()} ${error.response.config?.url}`, {
        statusCode: error.response.status,
        method: error.response.config?.method,
        url: error.response.config?.url,
        responseMessage: error.response.data?.message,
      });
    }

    if (error?.response?.status === 402) {
      return Promise.reject(error);
    }
    if (error?.response?.status === 403 && error?.response?.data?.errorCode === 'PERMISSION_DENIED') {
      return Promise.reject(new Error(error.response.data.message || 'You do not have permission to make this change.'));
    }
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      if (error?.response?.data?.errorCode !== 'FAILED_LOGIN' && !shouldBypassLoginRedirect(error)) {
        if (error?.response?.config?.url?.includes('/bo/')) {
          window.location.href = ROUTE_BACK_OFFICE_LOGIN;
        } else {
          window.location.href = ROUTE_LOGIN;
        }
      }
    }
    if (error?.response?.status === 413) {
      return Promise.reject(new Error('The content is too large. Please reduce the size.'));
    }
    return Promise.reject(error);
  }
);

export const callAuthAPI = async (endpoint, data = undefined, method = apiConstants.METHOD_GET, withCredentials = false) => {
  return axios({ method, url: `${apiConstants.SERVER_URL}/auth/${endpoint}`, data, withCredentials });
};

export const callUIAPI = async (endpoint, input = undefined, method = apiConstants.METHOD_GET, withCredentials = false, extraConfig = undefined) => {
  const url = `${apiConstants.SERVER_URL}/api/ui/${endpoint}`;
  // Normalised because the comparisons below decide whether `input` is sent at
  // all, and the METHOD_* constants are lowercase: an uppercase 'POST' used to
  // match neither branch and the request went out with no body.
  const normalizedMethod = String(method).toLowerCase();
  const axiosConfig = { ...(extraConfig || {}), method: normalizedMethod, url, withCredentials };
  if (normalizedMethod === apiConstants.METHOD_GET && !!input) {
    axiosConfig.params = input;
  } else if ((normalizedMethod === apiConstants.METHOD_POST || normalizedMethod === apiConstants.METHOD_PUT) && !!input) {
    axiosConfig.data = input;
  }
  console.log(`apiUtils.callUIAPI - About to make a ${normalizedMethod.toUpperCase()} request to ${url}${!!input ? ' with input: ' + JSON.stringify(input) : ''}`);
  return axios(axiosConfig);
};

export const callUIAPIStream = async (endpoint, input = undefined, method = apiConstants.METHOD_POST, signal = undefined) => {
  const url = `${apiConstants.SERVER_URL}/api/ui/${endpoint}`;
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: input ? JSON.stringify(input) : undefined,
    signal,
  });

  if (response?.status === 401 || response?.status === 403) {
    const isBackOffice = window.location.pathname?.includes('/bo/');
    window.location.href = isBackOffice ? ROUTE_BACK_OFFICE_LOGIN : ROUTE_LOGIN;
  }

  return response;
};

export const isSuccessCode = (code) => {
  return code >= 200 && code < 300;
};

export const executeAICommand = async (command, content, question, screenName, entityName, recordId, context) => {
  console.log('apiUtils.executeAICommand - input:', command, content, question, screenName, entityName, recordId, context);
  return callUIAPI(
    'generatecontent/aiCommand',
    { command, content, question, screenName, entityName, recordId, context },
    apiConstants.METHOD_POST,
    true
  )
    .then(response => {
      console.log('apiUtils.executeAICommand - response:', response);
      return response.data;
    })
    .catch(error => {
      console.error('apiUtils.executeAICommand - error:', error);
      if (error.code === 'ECONNABORTED') {
        throw new Error('Command timed out. Please try again.');
      }
      const failure = new Error('Command failed. Please try again.');
      failure.errorCode = error?.response?.data?.errorCode;
      throw failure;
    });
};